import CircleIcon from "@mui/icons-material/Circle";
import { Checkbox, FormControlLabel, Grid, Typography } from "@mui/material";
import { NoData } from "components/dataDisplay/NoData";
import { type Dispatch, type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { type ColorCieLab, labToCss } from "service/color-math";
import { OemPaginatedAutocomplete } from "./OemPaginatedAutocomplete";
import {
  effectiveOemMatch,
  isOemPickWithoutLab,
  type OemColorCodeOption,
  type OemColorMatch,
  type OemColorValue,
  type OemLibraryOption,
} from "./oem-color";

export interface OemColorCodeFilterProps {
  onChange: Dispatch<OemColorValue>;
  value?: OemColorValue;
}

const DEFAULT_VALUE: OemColorValue = { match: "exact" };

const OemColorPreview: FC<{ cieLab: ColorCieLab | null }> = ({ cieLab }) =>
  cieLab ? (
    <CircleIcon
      sx={{ color: labToCss(cieLab), marginRight: "var(--spacing8, 8px)" }}
    />
  ) : (
    <Typography
      component="span"
      color="text.secondary"
      aria-label="?"
      sx={{
        display: "inline-flex",
        justifyContent: "center",
        width: "1.5rem",
        marginRight: "var(--spacing8, 8px)",
      }}
    >
      ?
    </Typography>
  );

export const OemColorCodeFilter: FC<OemColorCodeFilterProps> = ({
  value = DEFAULT_VALUE,
  onChange,
}) => {
  const { t } = useTranslation();
  const { library, code, name, cieLab, match } = value;

  const selectedLibrary = useMemo<OemLibraryOption | null>(
    () => (library ? { name: library, value: library } : null),
    [library],
  );
  const selectedCode = useMemo<OemColorCodeOption | null>(
    () =>
      library && (code || name)
        ? {
            groupName: library,
            value: code ?? null,
            name: name ?? null,
            cieLab: cieLab ?? null,
          }
        : null,
    [library, code, name, cieLab],
  );
  const codeParams = useMemo(
    () => (library ? { oemNameImagio: library } : undefined),
    [library],
  );

  const onLibraryChange = (option: OemLibraryOption | null) =>
    onChange({ library: option?.value, match });

  const onCodeChange = (option: OemColorCodeOption | null) =>
    onChange({
      library,
      code: option?.value ?? undefined,
      name: option?.name ?? undefined,
      cieLab: option?.cieLab ?? undefined,
      match,
    });

  const onMatchChange = (checked: boolean) => {
    const nextMatch: OemColorMatch = checked ? "similar" : "exact";
    onChange({ ...value, match: nextMatch });
  };

  const codeLabel = (option: OemColorCodeOption) =>
    `${option.value ?? t("no_code")} / ${option.name ?? t("no_name")}`;

  return (
    <>
      <OemPaginatedAutocomplete<OemLibraryOption>
        entityType="oemColorLibrary"
        label={t("filter_oem_library")}
        value={selectedLibrary}
        onChange={onLibraryChange}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, selected) =>
          option.value === selected.value
        }
        renderOptionContent={(option) => option.name}
        noResultsText={(query) =>
          query
            ? t("filter_oem_library_no_results", { query })
            : t("filter_oem_library_empty")
        }
        testId="oem-library-filter"
      />
      <OemPaginatedAutocomplete<OemColorCodeOption>
        key={library ?? ""}
        entityType="oemColorCodeImagio"
        params={codeParams}
        label={t("filter_color_code_oem")}
        value={selectedCode}
        onChange={onCodeChange}
        disabled={!library}
        getOptionLabel={codeLabel}
        isOptionEqualToValue={(option, selected) =>
          option.value === selected.value && option.name === selected.name
        }
        renderValueAdornment={(selected) => (
          <OemColorPreview cieLab={selected.cieLab} />
        )}
        renderOptionContent={(option) => (
          <>
            <OemColorPreview cieLab={option.cieLab} />
            {option.value ?? <NoData label={t("no_code")} />}
            <Typography ml={0.5} mr={0.5}>
              /
            </Typography>
            {option.name ?? <NoData label={t("no_name")} />}
          </>
        )}
        noResultsText={(query) =>
          query
            ? t("filter_oem_code_no_results", { library, query })
            : t("filter_oem_code_empty", { library })
        }
        testId="oem-code-filter"
      />
      <Grid size={{ xs: 12, md: 6, xl: 3 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={effectiveOemMatch(value) === "similar"}
              disabled={isOemPickWithoutLab(value)}
              onChange={(event) => onMatchChange(event.target.checked)}
            />
          }
          label={t("filter_oem_include_similar")}
        />
      </Grid>
    </>
  );
};
